import { Box, Button, Icon } from "@outschool/backpack";
import { faPaperPlane } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { MAX_TEXT_LENGTH } from "@outschool/text";
import { TrackedButton } from "@outschool/ui-components-shared";
import { Space } from "@outschool/ui-legacy-component-library";
import React from "react";

import type { AttachmentFragmentFragment } from "../../..";
import { useReplyToContext } from "../../../providers/ReplyToProvider";
import EditableMessageContent, {
  EditableMessageContentSubmitType,
} from "../../EditableMessageContent";

type MentionsDataItem = {
  id: string;
  display: string;
};

type onSubmitProps = {
  text: string;
  attachments?: AttachmentFragmentFragment[] | null;
  video?: AttachmentFragmentFragment | null;
  mentions?: MentionsDataItem[] | null;
};

export interface AddClassPostCommentInputProps {
  placeholder?: string;
  mentionNames?: MentionsDataItem[];
  onSubmit(props: onSubmitProps): Promise<void>;
  /** Single-line input with an inline Post button and no attachments. */
  compact?: boolean;
}

export default function AddClassPostCommentInput({
  placeholder,
  mentionNames,
  onSubmit,
  compact = false,
}: AddClassPostCommentInputProps) {
  const { t } = useTranslation(
    "ui-components-classroom\\classroom\\ClassPostComment\\AddClassPostCommentInput",
  );
  const { messageContents, clearReplyTo } = useReplyToContext();
  const handleCommentSubmit = async ({
    text,
    attachments,
    video,
    mentions,
  }: EditableMessageContentSubmitType) => {
    try {
      await onSubmit({
        text,
        attachments,
        video,
        mentions,
      });
      clearReplyTo();
      return true;
    } catch (error) {
      return false;
    }
  };
  if (compact) {
    return (
      <Box
        data-test-id="AddClassPostComment"
        sx={{
          width: "100%",
          borderTop: "1px solid",
          borderColor: "neutral.200",
          paddingTop: 16,
          "& textarea:focus, & textarea:focus-visible": {
            outline: "none",
            boxShadow: "none",
          },
        }}
      >
        <EditableMessageContent
          useNewMessagingDesign
          inClassContext
          showAttachments={false}
          placeholder={placeholder ?? `Write a comment`}
          messageContent={messageContents}
          onSubmit={handleCommentSubmit}
          classMentionsData={mentionNames}
          maxLength={MAX_TEXT_LENGTH}
          renderButton={({ submit, disabled, reset }) => (
            <TrackedButton
              variant="contained"
              disabled={disabled}
              trackingName="send_comment"
              startIcon={<Icon icon={faPaperPlane} />}
              onClick={async () => {
                const success = await submit();
                if (success) {
                  reset();
                }
              }}
            >
              {t`Post`}
            </TrackedButton>
          )}
          // eslint-disable-next-line
          autoFocus={false}
          expandedRows={4}
        />
      </Box>
    );
  }

  return (
    <Box
      data-test-id="AddClassPostComment"
      sx={{
        width: "100%",
      }}
    >
      <EditableMessageContent
        collapsedUntilFocused
        inClassContext
        placeholder={placeholder ?? `Write a comment`}
        messageContent={messageContents}
        onSubmit={handleCommentSubmit}
        classMentionsData={mentionNames}
        maxLength={MAX_TEXT_LENGTH}
        renderButton={({ submit, disabled, reset }) => {
          return (
            <Box
              flex
              sx={{
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <TrackedButton
                variant="contained"
                disabled={disabled}
                trackingName="send_comment"
                onClick={async () => {
                  const success = await submit();
                  if (success) {
                    reset();
                  }
                }}
              >
                {t`Send`}
              </TrackedButton>
              <Space x="medium" />
              <Button
                variant="link"
                onClick={() => {
                  reset();
                }}
              >
                {t`Cancel`}
              </Button>
            </Box>
          );
        }}
        // eslint-disable-next-line
        autoFocus={false}
        expandedRows={10}
      />
    </Box>
  );
}
